import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { BeforeAfterDiff } from '@/components/shared/BeforeAfterDiff';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';
import { splitAtCut, titleLengthState, TILE_TITLE_CUT, TITLE_MAX_LENGTH } from '@/lib/titleCut';
import { countTitleDiff } from '@/lib/titleDiffCount';
import { ValidationNotice } from '../ValidationNotice';
import { ChangeSummary } from './ChangeSummary';
import { ScorePanel } from './ScorePanel';
import { AttributeChips, CHIP_FIELDS } from './AttributeChips';
import {
  CollapsibleField,
  listenSchluessel,
  summarizeList,
  summarizeText,
} from './CollapsibleField';
import type { WorkbenchProduct, WorkbenchEditState } from '@/hooks/useWorkbench';

interface FeedColumnProps {
  product: WorkbenchProduct;
  editState: WorkbenchEditState;
  onEditChange: (field: keyof WorkbenchEditState, value: string | string[]) => void;
}

/** Die Form, in der Pass 2 seine strukturierten Produktdetails ablegt. */
type DetailSection = {
  section?: string;
  section_name?: string;
  attributes?: Array<{ label?: string; name?: string; value: string }>;
};

/**
 * Der erklaerende Satz zur Kuerzungsmarke.
 *
 * Er ist in BEIDEN Fassungen noetig. Ein Strich ohne Erklaerung ist ein
 * senkrechter Balken mitten im Text.
 */
function CutMarkHint() {
  const { t } = useTranslation('workbench');
  return <p className="text-[10px] text-muted-foreground">{t('fields.cutMark')}</p>;
}

/**
 * Die Kuerzungsmarke als EIGENER Block, unter dem Diff.
 *
 * Der ganze neue Titel steht hier noch einmal am Stueck. Das ist die Fassung,
 * die Entscheidung E5 festgelegt hat - und zugleich die zweite von zwei
 * vollstaendigen Ausgaben desselben Strings auf derselben Spalte.
 *
 * 70 ist eine ANZEIGEKONVENTION, keine API-Grenze (siehe `titleCut.ts`). Die
 * Marke erscheint nur, wenn wirklich geschnitten wird.
 */
/**
 * Die Laengen-Warnung im LESEZUSTAND (B10, Werkbank-Entlastung 08.09.2026).
 *
 * Bis dahin sagte nur `TitleCharCounter` etwas ueber die Titellaenge - und
 * der rendert AUSSCHLIESSLICH im Bearbeiten-Modus. Wer eine Karte nur liest
 * (der Normalfall bei der Sammelfreigabe), sah nie, dass ein Titel nah am
 * oder ueber dem Google-Limit liegt.
 *
 * Erscheint ab `titleLengthState !== 'ok'`, also ab 140 Zeichen (`near`) UND
 * darueber (`over`) - anders als im Bearbeiten-Modus, wo `near` bewusst OHNE
 * Satz bleibt (der Zaehler daneben traegt die Zahl schon). Im Lesezustand
 * gibt es keinen Zaehler, der Satz ist hier die einzige Quelle.
 *
 * KEIN `aria-live`: der Satz steht still da und aendert sich nicht waehrend
 * des Lesens (anders als der Zaehler, der bei jedem Tastenanschlag mitgeht).
 */
function TitleLengthNotice({
  title,
  /**
   * Steht dieselbe Aussage schon im Hinweisband darunter?
   *
   * Die Regel `title_too_long` aus `_shared/product-validation.ts` sagt
   * woertlich dasselbe, und beide Bloecke stehen unmittelbar untereinander:
   * live gesehen am 08.09.2026 auf Spielgigant "Titel ueberschreitet das
   * Google-Limit von 150 Zeichen." direkt ueber "Titel ist laenger als 150
   * Zeichen und wird beim Hochladen abgeschnitten". Zweimal dieselbe Frage
   * beantwortet ist genau das Muster, das dieser Umbau abbaut (Spec 4.2,
   * eine Liste je Frage).
   *
   * Der Satz bleibt trotzdem, denn er ist NICHT ueberfluessig: das Band kommt
   * aus `validation_warnings`, also aus dem letzten Score-Lauf. Wer einen
   * Titel gerade von Hand ueber die Grenze geschrieben hat, hat dort noch
   * keinen Eintrag, und ausgerechnet dann ist die Aussage neu. Der
   * Beinahe-Fall (ab 140) hat im Band ohnehin keine Entsprechung.
   */
  bandSagtEsSchon,
}: {
  title: string;
  bandSagtEsSchon: boolean;
}) {
  const { t } = useTranslation('workbench');
  const zustand = titleLengthState(title);
  if (zustand === 'ok') return null;
  if (zustand === 'over' && bandSagtEsSchon) return null;

  return (
    <p
      data-testid="title-length-notice"
      className={`text-[10px] leading-snug ${
        zustand === 'over' ? 'text-destructive-ink' : 'text-warning-ink'
      }`}
    >
      {zustand === 'over'
        ? t('fields.titleLengthOver', { max: TITLE_MAX_LENGTH })
        : t('fields.titleLengthNear', { max: TITLE_MAX_LENGTH })}
    </p>
  );
}

function TitleCutMark({ title }: { title: string }) {
  const { visible, hidden, isCut } = splitAtCut(title);
  if (!isCut) return null;

  return (
    <div data-testid="title-cut" className="space-y-0.5 rounded-md bg-muted/30 px-2 py-1.5">
      <p className="text-xs leading-snug">
        <span>{visible}</span>
        <span aria-hidden="true" className="mx-0.5 font-bold text-warning-ink">
          |
        </span>
        <span className="opacity-[0.38]">{hidden}</span>
      </p>
      <CutMarkHint />
    </div>
  );
}

/**
 * Der Zaehler im Bearbeiten-Modus.
 *
 * Er sitzt INNERHALB des Editor-Blocks, nicht daneben: das `onBlur` am
 * umschliessenden `div` schliesst den Editor, sobald der Fokus den Block
 * verlaesst.
 *
 * Zwei Zahlen, zwei Einheiten, kein Widerspruch (Begruendung in
 * `titleCut.ts`): `chars` ist die UTF-16-Laenge, in der die 150 serverseitig
 * durchgesetzt wird; `visible` sind Codepunkte aus `splitAtCut`, also genau
 * die Zahl, die auch der Strich in der Kuerzungsmarke meint. Der zweite
 * Halbsatz erscheint nur, wenn wirklich geschnitten wird - dann ist 70
 * zwangslaeufig kleiner als die Laenge.
 *
 * RUHIG im Normalfall: 99,83 Prozent der optimierten Titel sind laenger als
 * 70 Zeichen. Ein Zaehler, der das faerbt, warnt fast immer und wird deshalb
 * nicht mehr gelesen. Gefaerbt wird erst ab 140 (`near`, ohne Satz) und ueber
 * 150 (`over`, mit Satz).
 */
function TitleCharCounter({
  title,
  id,
  strichSichtbar,
}: {
  title: string;
  id: string;
  /**
   * Ob eine Kuerzungsmarke waehrend des Bearbeitens ueberhaupt einen Traeger
   * hat. Kommt vom Aufrufer als `eigenerBlock` - derselbe Wert, der auch die
   * Anzeige von `TitleCutMark` gattert (`{eigenerBlock && <TitleCutMark .../>}`).
   * Keine zweite Rechnung, und seit dem dritten Schalterwert AUSDRUECKLICH
   * NICHT `!imDiff`: bei `keine` waere `!imDiff` wahr, obwohl es nirgends
   * einen Strich gibt. Bei `imNachherBlock` sitzt die Marke IM Diff, und der
   * Diff ist waehrend des Bearbeitens durch das Textfeld ersetzt - dort gibt
   * es ebenfalls keinen Strich.
   */
  strichSichtbar: boolean;
}) {
  const { t } = useTranslation('workbench');
  const { isCut } = splitAtCut(title);
  const zustand = titleLengthState(title);

  // Die `*-ink`-Tokens, nicht die Flaechentokens: `--destructive` misst auf
  // `--card` 3,78:1 und reisst damit bei 10 px die geforderten 4,5:1 (AA gilt
  // hier, "grosser Text" beginnt erst bei 18,66 px). `--destructive-ink` misst
  // 6,41:1, `--warning-ink` 5,04:1. Ausgerechnet der Zustand, der am
  // dringendsten gelesen werden muss, war der am schlechtesten lesbare.
  const farbe =
    zustand === 'over'
      ? 'text-destructive-ink'
      : zustand === 'near'
        ? 'text-warning-ink'
        : 'text-muted-foreground';

  return (
    <div
      className="space-y-0.5"
      /* Ein Mousedown auf ein nicht fokussierbares Element nimmt der Textarea
         den Fokus, `relatedTarget` ist dann null, und das `onBlur` des
         Editor-Blocks schliesst den Editor. Wer die Zahl markieren will oder
         beim Zielen auf "Fertig" danebentippt, stand danach wieder am
         Textanfang (`autoFocus` setzt die Einfuegemarke auf 0). */
      onMouseDown={(e) => e.preventDefault()}
    >
      <p id={id} data-testid="title-char-count" className={`text-[10px] tabular-nums ${farbe}`}>
        {isCut
          ? t('fields.titleCharsCut', {
              chars: title.length,
              max: TITLE_MAX_LENGTH,
              visible: TILE_TITLE_CUT,
            })
          : t('fields.titleChars', { chars: title.length, max: TITLE_MAX_LENGTH })}
      </p>
      {/* Die Live-Region ist IMMER montiert und wechselt nur ihren Inhalt.
          Wird eine Region gemeinsam mit ihrem Text neu eingehaengt, sagen JAWS
          und NVDA sie haeufig gar nicht an - sie lag zum Zeitpunkt der
          Mutation noch nicht im Accessibility-Tree. Leer hat das Element keine
          Zeilenbox und damit keine Hoehe.
          Der Satz traegt bewusst KEINE laufende Zahl: so meldet der
          Screenreader genau einmal, wenn die Grenze reisst, statt bei jedem
          Tastenanschlag erneut. Der Zaehler darueber hat aus demselben Grund
          kein aria-live. */}
      <p
        role="status"
        data-testid="title-over-limit"
        className="text-[10px] leading-snug text-destructive-ink"
      >
        {zustand === 'over' ? t('fields.titleCharsOver', { max: TITLE_MAX_LENGTH }) : ''}
      </p>
      {/* Mockup C2Edit: der Satz steht genau hier, im Bearbeiten-Zustand.
          Er ist der Moment, in dem sich die zwei Spalten erklaeren - beide
          lesen denselben `editState`, die Kachel links folgt also wirklich
          bei jedem Anschlag. Ohne den Hinweis schaut beim Tippen niemand nach
          links, und die ganze Anordnung zahlt sich nicht aus.

          ZUSTANDSABHAENGIG (Befund aus dem Abschluss-Review): den Strich darf
          der Satz nur nennen, wenn waehrend des Bearbeitens wirklich einer da
          ist. Das gilt nur bei `kuerzungsAnzeige: 'eigenerBlock'` - dort
          rendert `TitleCutMark` unabhaengig vom Bearbeiten-Zustand
          (`{!imDiff && <TitleCutMark .../>}` weiter unten). Bei
          `imNachherBlock` sitzt die Marke IM Diff, und der Diff wird waehrend
          des Bearbeitens durch das Textfeld ersetzt - dort gibt es keinen
          Strich, und ein Satz, der ihn behauptet, waere falsch.

          Kein aria-live: eine gleichbleibende Erklaerung gehoert nicht in die
          Ansage-Warteschlange, in der schon der Grenzhinweis darueber
          steht. */}
      <p className="text-[10px] leading-snug text-muted-foreground">
        {strichSichtbar ? t('fields.titleEditHint') : t('fields.titleEditHintOhneStrich')}
      </p>
    </div>
  );
}

function HighlightsEditor({
  editState,
  onEditChange,
  before,
}: {
  editState: WorkbenchEditState;
  onEditChange: FeedColumnProps['onEditChange'];
  before: string[];
}) {
  const { t } = useTranslation('workbench');
  const { highlightsEditor } = useEntwurf();
  const markenId = useId();
  const [editing, setEditing] = useState(highlightsEditor === 'wieHeute');
  const anpassenRef = useRef<HTMLButtonElement>(null);
  const list = editState.optimized_product_highlights;

  /* Dieselbe Funktion, die auch die Kopfzahl rechnet - sonst kann die Zahl
     oben den Marken unten widersprechen. */
  const vorher = new Set(before.map(listenSchluessel).filter((k) => k.length > 0));
  const istNeu = (h: string) => h.trim().length > 0 && !vorher.has(listenSchluessel(h));

  /* Die Merchant-Liste steht in BEIDEN Fassungen oben - sie ist der Bezug,
     gegen den "neu" ueberhaupt etwas heisst. Unveraendert aus dem Bestand. */
  const merchantListe =
    before.length > 0 ? (
      <div className="space-y-1">
        <h5 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('fields.highlightsBefore')}
        </h5>
        <ul className="space-y-0.5">
          {before.map((h, i) => (
            <li key={`${h}-${i}`} className="text-xs leading-snug text-muted-foreground">
              {h}
            </li>
          ))}
        </ul>
      </div>
    ) : (
      <p className="text-[11px] leading-snug text-muted-foreground">
        {t('fields.highlightsNoneBefore')}
      </p>
    );

  return (
    <div className="space-y-3">
      {merchantListe}

      {editing ? (
        <div className="space-y-1">
          {/* Der bestehende Editor-Block, unveraendert uebernommen, nur mit
              istNeu() statt der zweimal inline wiederholten Bedingung. */}
          {list.map((h, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={h}
                aria-label={`${t('fields.highlights')} ${i + 1}`}
                aria-describedby={istNeu(h) ? `${markenId}-${i}` : undefined}
                onChange={(e) => {
                  const next = [...list];
                  next[i] = e.target.value;
                  onEditChange('optimized_product_highlights', next);
                }}
                className="h-8 flex-1 py-0 text-base md:text-sm"
              />
              {/* Die Marke steht VOR dem Entfernen-Knopf und haengt per
                  aria-describedby am Eingabefeld. Danach gelesen beschriebe
                  sie den Knopf statt den Wert. */}
              {istNeu(h) ? (
                <span
                  id={`${markenId}-${i}`}
                  className="flex-shrink-0 rounded-full bg-success/15 px-1.5 py-0.5 text-[10px] font-medium text-success-ink"
                >
                  {t('fields.highlightNew')}
                </span>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 flex-shrink-0 px-2 text-xs text-muted-foreground hover:text-destructive-ink"
                onClick={() =>
                  onEditChange(
                    'optimized_product_highlights',
                    list.filter((_, k) => k !== i),
                  )
                }
              >
                {t('fields.removeHighlight')}
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground"
            onClick={() => onEditChange('optimized_product_highlights', [...list, ''])}
          >
            {t('fields.addHighlight')}
          </Button>
        </div>
      ) : (
        <div data-testid="highlights-readonly" className="space-y-1">
          <ul className="space-y-0.5">
            {list
              .filter((h) => h.trim().length > 0)
              .map((h, i) => (
                <li key={`${h}-${i}`} className="flex items-start gap-2 text-xs leading-snug">
                  <span className="min-w-0 flex-1">{h}</span>
                  {istNeu(h) && (
                    <span className="flex-shrink-0 rounded-full bg-success/15 px-1.5 py-0.5 text-[10px] font-medium text-success-ink">
                      {t('fields.highlightNew')}
                    </span>
                  )}
                </li>
              ))}
          </ul>
          <Button
            variant="ghost"
            size="sm"
            ref={anpassenRef}
            data-testid="workbench-edit-highlights"
            aria-label={t('actions.editFieldAria', { field: t('fields.highlights') })}
            aria-expanded={false}
            className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
            onClick={() => setEditing(true)}
          >
            <Pencil className="mr-1 h-3 w-3" />
            {t('actions.editField')}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Der Beschreibungsblock im Aufklapper.
 *
 * Zwei Fassungen, gesteuert von `beschreibungEditor`. Der Fokusvertrag ist
 * derselbe wie beim Titel: der "Anpassen"-Knopf ersetzt sich durch den Editor,
 * "Fertig" macht es umgekehrt, und beide Male verschwindet der fokussierte
 * Knoten. Ohne Rueckgabe faellt der Fokus auf <body>.
 *
 * `warEditingRef` verhindert, dass der Erst-Render den Fokus stiehlt. Der
 * Blur-Pfad braucht die Rueckgabe nicht und darf sie auch nicht: dort ist der
 * Fokus bereits dort angekommen, wo der Nutzer hinwollte.
 */
function DescriptionBlock({
  product,
  editState,
  onEditChange,
}: {
  product: WorkbenchProduct;
  editState: WorkbenchEditState;
  onEditChange: FeedColumnProps['onEditChange'];
}) {
  const { t } = useTranslation('workbench');
  const { beschreibungEditor } = useEntwurf();
  const [editing, setEditing] = useState(false);
  const anpassenRef = useRef<HTMLButtonElement>(null);
  const warEditingRef = useRef(false);
  const [fokusZurueck, setFokusZurueck] = useState(false);

  useEffect(() => {
    if (editing) {
      warEditingRef.current = true;
      return;
    }
    if (warEditingRef.current && fokusZurueck) {
      warEditingRef.current = false;
      setFokusZurueck(false);
      anpassenRef.current?.focus();
    } else if (warEditingRef.current) {
      warEditingRef.current = false;
    }
  }, [editing, fokusZurueck]);

  const diff = (
    <BeforeAfterDiff
      before={product.description ?? ''}
      after={editState.optimized_description}
      variant="stacked"
    />
  );

  const feld = (
    <Textarea
      value={editState.optimized_description}
      /* Bei JEDEM Anschlag nach oben. Kein lokaler Entwurf: sonst verwirft ein
         Klick daneben ihn still und die beforeunload-Warnung feuert nicht
         (Befund 3 der Bestandsaufnahme). */
      onChange={(e) => onEditChange('optimized_description', e.target.value)}
      rows={8}
      className="mt-2 resize-y text-base md:text-sm"
      aria-label={t('actions.editFieldAria', { field: t('fields.description') })}
    />
  );

  if (beschreibungEditor === 'wieHeute') {
    return (
      <>
        {diff}
        {feld}
      </>
    );
  }

  if (editing) {
    return (
      <div
        className="space-y-1.5"
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setEditing(false);
        }}
      >
        {feld}
        <Button
          variant="ghost"
          size="sm"
          data-testid="workbench-done-description"
          className="h-7 px-2 text-xs text-muted-foreground"
          onClick={() => {
            // Nur ueber den Knopf den Fokus zuruecknehmen. Beim Blur ist er
            // schon bei dem Element, zu dem der Nutzer wollte.
            setFokusZurueck(true);
            setEditing(false);
          }}
        >
          {t('actions.doneEditing')}
        </Button>
      </div>
    );
  }

  return (
    <>
      {diff}
      <Button
        variant="ghost"
        size="sm"
        ref={anpassenRef}
        data-testid="workbench-edit-description"
        aria-label={t('actions.editFieldAria', { field: t('fields.description') })}
        aria-expanded={false}
        className="mt-2 h-7 px-2 text-xs text-muted-foreground hover:text-primary"
        onClick={() => setEditing(true)}
      >
        <Pencil className="mr-1 h-3 w-3" />
        {t('actions.editField')}
      </Button>
    </>
  );
}

/**
 * Rechte Spalte: was wirklich im Feed steht.
 *
 * Eine einzige Spalte, sortiert nach Entscheidungsgewicht. Der Titel steht
 * oben, weil er die Entscheidung traegt; die drei Langfelder stehen unten und
 * eingeklappt, weil sie sie selten kippen - aber sie stehen da, denn alle drei
 * gehen zu Google und zwei davon zaehlen im Score (Beschreibung 20 Prozent,
 * Details 15, Highlights 10).
 */
export function FeedColumn({ product, editState, onEditChange }: FeedColumnProps) {
  const { t } = useTranslation('workbench');
  const [editingTitle, setEditingTitle] = useState(false);

  /**
   * Der "Anpassen"-Schalter ersetzt sich beim Klick durch den Editor, und
   * "Fertig" macht es umgekehrt. Beide Male verschwindet der fokussierte
   * Knoten - ohne Rueckgabe faellt der Fokus auf <body>, und ein
   * Tastaturnutzer beginnt wieder ganz oben auf der Seite.
   *
   * `warEditingRef` verhindert, dass der Erst-Render den Fokus stiehlt.
   * Der Blur-Pfad (Fokus verlaesst den Block) braucht das nicht und darf es
   * auch nicht: dort ist der Fokus bereits woanders angekommen.
   */
  const anpassenRef = useRef<HTMLButtonElement>(null);
  const warEditingRef = useRef(false);
  const [fokusZurueck, setFokusZurueck] = useState(false);
  useEffect(() => {
    if (editingTitle) {
      warEditingRef.current = true;
      return;
    }
    if (warEditingRef.current && fokusZurueck) {
      warEditingRef.current = false;
      setFokusZurueck(false);
      anpassenRef.current?.focus();
    } else if (warEditingRef.current) {
      warEditingRef.current = false;
    }
  }, [editingTitle, fokusZurueck]);
  // `useId`, damit `aria-describedby` auch dann eindeutig bleibt, wenn die
  // Karte je gedoppelt gerendert wird.
  const zaehlerId = useId();

  const { kuerzungsAnzeige, titelDiffTraeger, insMarke, feedSpalteDichte, titelAnordnung, punkteOrt } =
    useEntwurf();
  const [punkteOffen, setPunkteOffen] = useState(false);
  const kompakt = feedSpalteDichte === 'kompakt';
  const imDiff = kuerzungsAnzeige === 'imNachherBlock';
  const eigenerBlock = kuerzungsAnzeige === 'eigenerBlock';
  const wirdGeschnitten = splitAtCut(editState.optimized_title).isCut;
  /* Vorher NUR aus original_title (Spec Datenintegritaet Original-Titel 5.4).
     NULL oder leer heisst: kein sauberes Original bekannt - dann kein Diff,
     kein Zaehler, nur der optimierte Titel mit einer Notiz. product.title ist
     keine Quelle: die Spalte trug bei rund 4.600 Produkten unseren eigenen
     frueheren Output (09.09.2026). */
  const original = product.original_title && product.original_title.trim() !== '' ? product.original_title : null;
  /* `BeforeAfterDiff` ignoriert `cutAt` in `variant="inline"` (siehe dort) -
     ohne diese Bedingung stuende der Erklaersatz zum Strich da, obwohl der
     Diff selbst gar keinen zeigt. Reine Kombinations-Guard, keine neue Regel:
     STANDARD kombiniert `imNachherBlock` ohnehin nie mit `inline`. */
  const strichHatTraeger = titelAnordnung !== 'inline';

  /* Der Zaehler ist der Ersatztraeger. Er erscheint, sobald IRGENDEINE Farbe
     aus dem Titel-Diff verschwindet - sonst haette die Kombination "gruen weg,
     rot da" keinen Traeger fuer die Ergaenzungen. Er nennt darum immer beide
     Zahlen, nicht nur die der entfaerbten Seite. */
  const zaehlerNoetig =
    original !== null && (titelDiffTraeger === 'zaehlerImLabel' || insMarke !== 'gruen');
  // Perf-Minor aus dem Karten-Abschlussbericht, abgearbeitet zur
  // Befoerderung: der Wort-Diff lief bei jedem Render der Spalte.
  const diffZahlen = useMemo(
    () => countTitleDiff(original ?? '', editState.optimized_title),
    [original, editState.optimized_title],
  );
  const titelLabel = zaehlerNoetig ? (
    <span className="flex items-baseline gap-2">
      <span>{t('fields.title')}</span>
      <span data-testid="title-diff-count" className="text-[10px] normal-case tracking-normal">
        {diffZahlen.entfernt === 0 && diffZahlen.ergaenzt === 0
          ? t('fields.titleDiffNone')
          : t('fields.titleDiffCount', diffZahlen)}
      </span>
    </span>
  ) : (
    t('fields.title')
  );

  /**
   * Feldgebundene Hinweise stehen NUR am Chip (Spec 7); alle uebrigen bleiben
   * hier. Der Filter fragt nach einem gerenderten Chip, nicht nach der blossen
   * Anwesenheit eines Feldnamens: sonst verschwaenden `gtin_invalid`,
   * `title_too_long` und `image_too_small` spurlos, und ein Hinweis, der auf
   * einer Freigabeflaeche ersatzlos wegfaellt, ist der teuerste denkbare
   * Ausgang.
   */
  const bandWarnings = (product.validation_warnings ?? []).filter(
    (w) => !w.field || !CHIP_FIELDS.has(w.field),
  );
  /* Traegt das Hinweisband die Laengen-Aussage bereits? Gefragt ist das BAND,
     nicht die Rohliste: eine Regel, die als Chip gerendert wird, steht nicht
     unter dem Diff und doppelt den Satz deshalb auch nicht. */
  const bandNenntTitelLaenge = bandWarnings.some((w) => w.rule_id === 'title_too_long');

  const beforeHighlights = product.product_highlights ?? [];
  const details: DetailSection[] = Array.isArray(product.optimized_product_details)
    ? (product.optimized_product_details as DetailSection[])
    : [];
  const detailFieldCount = details.reduce((sum, s) => sum + (s.attributes?.length ?? 0), 0);

  /* Der "Anpassen"-Knopf, einmal gebaut: er sitzt entweder in der Label-Zeile
     des Diffs oder, ohne Original, in der Kopfzeile des Leerzustands - beide
     Male dieselbe Handlung, dieselbe testid, kein zweiter Bau. */
  const anpassenKnopf = (
    <Button
      variant="ghost"
      size="sm"
      ref={anpassenRef}
      data-testid="workbench-edit-title"
      aria-label={t('actions.editFieldAria', { field: t('fields.title') })}
      aria-expanded={false}
      className="h-7 flex-shrink-0 px-2 text-xs text-muted-foreground hover:text-primary"
      onClick={() => setEditingTitle(true)}
    >
      <Pencil className="mr-1 h-3 w-3" />
      {t('actions.editField')}
    </Button>
  );

  return (
    <div className={`flex flex-col ${kompakt ? 'gap-2.5' : 'gap-4'}`}>
      {/* 1. Titel */}
      <section className="space-y-2">
        {editingTitle ? (
          <div
            className="space-y-1.5"
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setEditingTitle(false);
            }}
          >
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('fields.title')}
            </p>
            <Textarea
              autoFocus
              data-testid="title-editor"
              aria-label={t('fields.title')}
              aria-describedby={zaehlerId}
              value={editState.optimized_title}
              onChange={(e) => onEditChange('optimized_title', e.target.value)}
              rows={2}
              className="resize-none text-base md:text-sm"
            />
            <TitleCharCounter
              title={editState.optimized_title}
              id={zaehlerId}
              strichSichtbar={eigenerBlock}
            />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={() => {
                // Nur ueber den Knopf den Fokus zuruecknehmen. Beim Blur ist
                // er schon bei dem Element, zu dem der Nutzer wollte.
                setFokusZurueck(true);
                setEditingTitle(false);
              }}
            >
              {t('actions.doneEditing')}
            </Button>
          </div>
        ) : (
          <>
            {original === null ? (
              /* Leerzustand: kein Vorher, das wir nicht belegen koennen. Label-
                 zeile wie im Diff, der optimierte Titel als Text, darunter die
                 Notiz. Kein Kuerzungsstrich (der haengt am Nachher-Kasten des
                 Diffs), die Laengen-Warnung bleibt (sie meint den Nachher-Text). */
              <div className="space-y-1.5" data-testid="title-no-original">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('fields.title')}
                  </p>
                  {anpassenKnopf}
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{editState.optimized_title}</p>
                <p className="text-[10px] text-muted-foreground">{t('fields.titleNoOriginal')}</p>
              </div>
            ) : (
              <BeforeAfterDiff
                before={original}
                /* Diff gegen den BEARBEITETEN Text: nach einer Korrektur soll
                   die Anzeige zeigen, was tatsaechlich hochgeladen wuerde. */
                after={editState.optimized_title}
                variant={
                  titelAnordnung === 'originalFussnote'
                    ? 'heldMitFussnote'
                    : titelAnordnung === 'inline'
                      ? 'inline'
                      : 'stacked'
                }
                label={titelLabel}
                /* Der "Anpassen"-Knopf steht rechts in der Label-Zeile statt
                   unter dem Diff (T5.3) - eine Aktion neben ihrem Feldnamen,
                   nicht danach. */
                labelSlot={anpassenKnopf}
                cutAt={imDiff ? TILE_TITLE_CUT : undefined}
                removedTone={titelDiffTraeger === 'zaehlerImLabel' ? 'neutral' : 'farbe'}
                addedTone={insMarke === 'gruen' ? undefined : insMarke}
              />
            )}
            {original !== null && imDiff && wirdGeschnitten && strichHatTraeger && <CutMarkHint />}
            <TitleLengthNotice
              title={editState.optimized_title}
              bandSagtEsSchon={bandNenntTitelLaenge}
            />
          </>
        )}
        {eigenerBlock && <TitleCutMark title={editState.optimized_title} />}
      </section>

      {/* 2. Hinweise, die zu keinem Chip gehoeren */}
      <ValidationNotice warnings={bandWarnings} />

      {/* 3. Was hat sich geaendert - traegt bei `punkteOrt: 'imAenderungsblock'`
          auch die Punktzahl: Badge rechts der Ueberschrift, Klick klappt den
          vollen Breakdown auf. Die Zahl steht dann bei ihrer Begruendung. */}
      <ChangeSummary
        product={product}
        editState={editState}
        scoreSlot={
          punkteOrt === 'imAenderungsblock' ? (
            <button
              type="button"
              data-testid="punkte-badge"
              aria-expanded={punkteOffen}
              aria-label={t('score.badgeAria')}
              onClick={() => setPunkteOffen((o) => !o)}
              className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-success/15 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-success-ink transition-colors hover:bg-success/25"
            >
              {typeof product.preTotalScore === 'number' && (
                <span className="font-normal text-muted-foreground line-through decoration-muted-foreground/40">
                  {Math.round(product.preTotalScore)}
                </span>
              )}
              {typeof product.totalScore === 'number' ? Math.round(product.totalScore) : '?'}
              <ChevronDown
                aria-hidden="true"
                className={`h-3 w-3 transition-transform ${punkteOffen ? 'rotate-180' : ''}`}
              />
            </button>
          ) : undefined
        }
        scorePanel={
          punkteOrt === 'imAenderungsblock' && punkteOffen ? (
            <ScorePanel
              post={product.componentScores}
              postTotal={product.totalScore}
              pre={product.preComponentScores}
              preTotal={product.preTotalScore}
              scoreType={product.scoreType}
              attributeApplicable={product.attributeApplicable}
              preCalculatedAt={product.preCalculatedAt}
              postCalculatedAt={product.postCalculatedAt}
            />
          ) : undefined
        }
      />

      {/* 4. Attribute als Chips */}
      <AttributeChips
        product={product}
        editState={editState}
        onEditChange={onEditChange}
        warnings={product.validation_warnings}
      />

      {/* 5. bis 7. Die Langfelder, eingeklappt */}
      <div className={kompakt ? 'space-y-1.5' : 'space-y-2'}>
        <CollapsibleField
          label={t('fields.description')}
          summary={summarizeText(product.description ?? '', editState.optimized_description, t)}
          fieldId="description"
        >
          <DescriptionBlock
            product={product}
            editState={editState}
            onEditChange={onEditChange}
          />
        </CollapsibleField>

        <CollapsibleField
          label={t('fields.highlights')}
          summary={summarizeList(beforeHighlights, editState.optimized_product_highlights, t)}
          fieldId="highlights"
        >
          <HighlightsEditor
            editState={editState}
            onEditChange={onEditChange}
            before={beforeHighlights}
          />
        </CollapsibleField>

        <CollapsibleField
          label={t('fields.productDetails')}
          summary={
            details.length === 0
              ? t('collapsible.empty')
              : t('collapsible.detailSections', {
                  count: details.length,
                  fields: detailFieldCount,
                })
          }
          fieldId="details"
        >
          {details.length === 0 ? (
            <p className="text-xs italic text-muted-foreground">{t('fields.emptyBoth')}</p>
          ) : (
            <div className="space-y-3">
              {/* Mockup C2Scroll: Produktdetails sind das einzige Langfeld
                  ohne Vorher-Bezug - `product_details` des Merchants wird
                  nicht geladen, und in aller Regel gab es dort auch nichts.
                  Ohne diesen Satz liest sich der leere Vorher-Teil wie ein
                  fehlender Diff statt wie die Auskunft "das ist komplett
                  neu, und es geht so zu Google". */}
              <p className="text-[11px] leading-snug text-muted-foreground">
                {t('fields.detailsIntro')}
              </p>
              {details.map((section, idx) => {
                // Pass-2 v3 schreibt { section, attributes:[{label, value}] },
                // das alte v1-Schema { section_name, attributes:[{name, value}] }.
                // Beide muessen gelesen werden, es liegen Zeilen aus beiden
                // Epochen in der Datenbank.
                const name = section.section || section.section_name;
                if (!name) return null;
                return (
                  <div key={`${name}-${idx}`} className="space-y-1">
                    <h5 className="text-xs font-semibold text-muted-foreground">{name}</h5>
                    <div className="grid gap-1">
                      {(section.attributes ?? []).map((attr, i) => (
                        <div
                          key={`${attr.label ?? attr.name}-${i}`}
                          className="grid grid-cols-2 gap-2 text-xs"
                        >
                          <span className="text-muted-foreground">{attr.label || attr.name}</span>
                          <span>{attr.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CollapsibleField>
      </div>
    </div>
  );
}
